import React, { useEffect, useState } from 'react';
import {
    Drawer, Box, Typography, IconButton, Grid, Dialog, DialogContent,
    Pagination, Snackbar, Alert, CircularProgress, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const ImageDrawer = ({ open, onClose, directory,files, maxSelection = 3, onSelectedChange }) => {
    const [images, setImages] = useState([]);
    const [selected, setSelected] = useState(files??[]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [previewImage, setPreviewImage] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [totalImages, setTotalImages] = useState(0);

    const fetchImages = async () => {
        if (!directory) return;
        setLoading(true);
        try {
            const root = document.getElementById('labeled-files-root');
            const baseUrl = root?.dataset?.imagesViewUrl?.replace(encodeURIComponent('[directory]'), encodeURIComponent(directory.replace(/^\//, '')));
            const res = await fetch(`${baseUrl}?page=${page}`);
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            const json = await res.json();

            const data = Object.values(json.data || {});
            setImages(data);
            setTotalPages(json.last_page || 1);
            setTotalImages(json.total || data.length);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to load images');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && directory) fetchImages();
    }, [open, directory, page]);

    useEffect(() => {
        if (onSelectedChange) onSelectedChange(selected);
    }, [selected]);

    const toggleSelect = (path) => {
        const exists = selected.find((f) => f === path);
        if (exists) {
            setSelected(selected.filter((f) => f !== path));
        } else if (selected.length < maxSelection) {
            setSelected([...selected, path]);
        }
    };

    return (
        <>
            <Drawer anchor="right" open={open} onClose={onClose}>
                <Box width={1000} p={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Select Images</Typography>
                        <IconButton onClick={onClose}><CloseIcon /></IconButton>
                    </Box>

                    <Box mb={2}>
                        <Typography variant="body2" color="textSecondary">
                            Selected: {selected.length} / {maxSelection} &nbsp;&bull;&nbsp; Total: {totalImages}
                        </Typography>
                        {selected.length > 0 && (
                            <Box mt={1}>
                                {selected.map((s) => (
                                    <Chip
                                        key={s}
                                        label={s.split('/').pop()}
                                        onDelete={() => setSelected(selected.filter((f) => f !== s))}
                                        sx={{ mr: 1, mb: 1 }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {loading ? (
                        <Box display="flex" justifyContent="center" mt={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {images.map((img) => {
                                const isSelected = selected.find((s) => s === img.path);
                                return (
                                    <Grid item xs={4} key={img.filename}>
                                        <Box position="relative">
                                            <Box
                                                component="img"
                                                src={img.url}
                                                alt={img.filename}
                                                sx={{
                                                    width: '100%',
                                                    border: isSelected ? '3px solid #d21935' : '2px solid transparent',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => toggleSelect(img.path)}
                                                onDoubleClick={() => setPreviewImage(img)}
                                            />
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    left: 8,
                                                    backgroundColor:  isSelected ?'#d21935':'#1976d2',
                                                    color: 'white',
                                                    padding: '2px 8px',
                                                    borderRadius: '16px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    pointerEvents: 'none'
                                                }}
                                            >
                                                {img.count}
                                            </Box>
                                            <Box>
                                                <Typography variant="body2">{img.filename}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}

                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            disabled={loading}
                        />
                    </Box>
                </Box>
            </Drawer>

            <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="md">
                <DialogContent>
		  <Box position="relative">
                    <Box component="img" src={previewImage?.fullurl} sx={{ width: '100%' }} />
		    <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    left: 8,
                                                    backgroundColor: '#1976d2',
                                                    color: 'white',
                                                    padding: '2px 8px',
                                                    borderRadius: '16px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    pointerEvents: 'none'
                                                }}
                                            >
                                                {previewImage?.count}
		    </Box>
		  </Box>
                </DialogContent>
            </Dialog>

            <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}>
                <Alert severity="error" onClose={() => setError('')}>
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ImageDrawer;
